import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Play, Square, Clock, History } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import shiftApi, { Shift } from '../services/shiftApi';
import fingerprintApi, { FingerprintDevice } from '../services/fingerprintApi';
import ShiftStartModal from '../components/ShiftStartModal';
import ShiftLockoutWarning from '../components/ShiftLockoutWarning';
import toast from 'react-hot-toast';

const EmployeeShift: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isConnected } = useWebSocket();
  const queryClient = useQueryClient();
  const [showStartModal, setShowStartModal] = useState(false);
  const [useFingerprint, setUseFingerprint] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);

  // Get employee ID from user
  const employeeId = user?.employee?.id;

  // Check if fingerprint is enabled
  const { data: fingerprintEnabled } = useQuery({
    queryKey: ['fingerprint-enabled'],
    queryFn: () => fingerprintApi.checkFingerprintEnabled(),
    enabled: !!user
  });

  const isFingerprintEnabled = fingerprintEnabled?.enabled || false;

  // Get available devices for current admin's tenant
  const { data: devicesData } = useQuery({
    queryKey: ['fingerprint-devices-my'],
    queryFn: () => fingerprintApi.getMyFingerprintDevices(),
    enabled: isFingerprintEnabled && !!user
  });

  const devices: FingerprintDevice[] = devicesData?.devices || [];

  // Auto-select first device when devices are loaded
  useEffect(() => {
    if (devices.length > 0 && !selectedDeviceId) {
      const firstActiveDevice = devices.find(d => d.status === 'active') || devices[0];
      if (firstActiveDevice) {
        setSelectedDeviceId(firstActiveDevice.deviceId);
      }
    }
  }, [devices, selectedDeviceId]);

  // Get active shift
  const { data: activeShiftData, isLoading: loadingShift } = useQuery({
    queryKey: ['active-shift', employeeId],
    queryFn: () => shiftApi.getActiveShift(employeeId!),
    enabled: !!employeeId,
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const activeShift: Shift | null = activeShiftData?.shift || null;

  // Get shift history
  const { data: historyData } = useQuery({
    queryKey: ['shift-history', employeeId],
    queryFn: () => shiftApi.getShiftHistory(employeeId!),
    enabled: !!employeeId && !activeShift
  });

  const shiftHistory: Shift[] = historyData?.shifts || [];

  // Start shift mutation
  const startShiftMutation = useMutation({
    mutationFn: (data: { machineIds: number[]; machineGroupIds: number[] }) =>
      shiftApi.startShift({
        employeeId: employeeId!,
        ...data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-shift', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['shift-history', employeeId] });
      toast.success('Shift started successfully');
      setShowStartModal(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to start shift');
    }
  });

  // End shift mutation
  const endShiftMutation = useMutation({
    mutationFn: (shiftId: number) => shiftApi.endShift(shiftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-shift', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['shift-history', employeeId] });
      toast.success('Shift ended successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to end shift');
    }
  });

  const handleStartShift = async () => {
    if (useFingerprint && isFingerprintEnabled) {
      try {
        // Check WebSocket connection
        if (!isConnected) {
          toast.error('WebSocket not connected. Please wait...');
          return;
        }

        // Check device selection
        if (!selectedDeviceId) {
          toast.error('Please select a fingerprint device first.');
          return;
        }

        // Get employee fingerprint template
        let employeeFingerprint;
        try {
          employeeFingerprint = await fingerprintApi.getEmployeeFingerprint(employeeId!);
        } catch (error: any) {
          if (error.response?.status === 404) {
            toast.error('No fingerprint enrolled. Please enroll your fingerprint first.');
            setUseFingerprint(false);
            setShowStartModal(true);
            return;
          }
          throw error;
        }

        if (!employeeFingerprint.employee.fingerprintTemplate) {
          toast.error('No fingerprint template found. Please enroll your fingerprint first.');
          setUseFingerprint(false);
          setShowStartModal(true);
          return;
        }

        toast.loading('Verifying fingerprint...', { id: 'fingerprint-verify' });

        // Verify fingerprint using socket-based API
        const verificationResult = await fingerprintApi.verifyFingerprint({
          deviceId: selectedDeviceId,
          fingerprintTemplate: employeeFingerprint.employee.fingerprintTemplate
        });

        toast.dismiss('fingerprint-verify');

        // If API call succeeds, verification was successful
        // The backend returns { message, employee } on success, or throws error on failure
        // Fingerprint verified successfully, proceed with shift start
        toast.success('Fingerprint verified successfully');
        setShowStartModal(true);
      } catch (error: any) {
        toast.dismiss('fingerprint-verify');
        toast.error(error.response?.data?.message || error.message || 'Fingerprint verification failed');
        
        // Fallback to manual login
        setUseFingerprint(false);
        setShowStartModal(true);
      }
    } else {
      // Manual login
      setShowStartModal(true);
    }
  };

  const handleStartShiftWithSelection = (machineIds: number[], machineGroupIds: number[]) => {
    startShiftMutation.mutate({ machineIds, machineGroupIds });
  };

  const handleEndShift = () => {
    if (activeShift) {
      if (window.confirm('Are you sure you want to end your shift?')) {
        endShiftMutation.mutate(activeShift.id);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : Date.now();
    const hours = Math.floor((endTime - startTime) / (1000 * 60 * 60));
    const minutes = Math.floor(((endTime - startTime) % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (!employeeId) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-gray-600">Employee information not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Shift</h1>
        <p className="text-gray-600">Manage your work shift and track time</p>
      </div>

      {loadingShift ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : activeShift ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Active Shift</h2>
              <p className="text-gray-600">
                Started: {formatDate(activeShift.startedAt)}
              </p>
              <p className="text-gray-600">
                Duration: {formatDuration(activeShift.startedAt)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <Clock className="w-4 h-4 mr-1" />
                Active
              </span>
            </div>
          </div>

          <ShiftLockoutWarning
            shiftId={activeShift.id}
            lockoutUntil={activeShift.lockoutUntil}
            isAdmin={user?.role === 'ADMIN' || user?.role === 'SUPERADMIN'}
          />

          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Assigned Machines/Lines</h3>
            <div className="grid grid-cols-2 gap-4">
              {activeShift.machines
                .filter(m => !m.unassignedAt)
                .map((machine) => (
                  <div
                    key={machine.id}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    {machine.machine ? (
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          Machine: {machine.machine.number}
                        </span>
                      </div>
                    ) : machine.machineGroup ? (
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          Line: {machine.machineGroup.name}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ))}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleEndShift}
              className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
            >
              <Square className="w-5 h-5" />
              End Shift
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-12">
            <Play className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Active Shift</h2>
            <p className="text-gray-600 mb-6">
              Start your shift to begin tracking your work time.
            </p>
            {isFingerprintEnabled && (
              <div className="mb-4">
                <label className="flex items-center justify-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    checked={useFingerprint}
                    onChange={(e) => setUseFingerprint(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Use Fingerprint Login</span>
                </label>
                {useFingerprint && (
                  <div className="mt-2">
                    {devices.length === 0 ? (
                      <p className="text-xs text-red-500 text-center mb-2">
                        No fingerprint devices available. Please contact administrator.
                      </p>
                    ) : (
                      <div className="mb-2">
                        <label className="block text-xs text-gray-700 mb-1 text-center">
                          Select Device:
                        </label>
                        <select
                          value={selectedDeviceId || ''}
                          onChange={(e) => setSelectedDeviceId(e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full max-w-xs mx-auto p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">-- Select Device --</option>
                          {devices.map((device) => (
                            <option key={device.id} value={device.deviceId}>
                              {device.name} (ID: {device.deviceId}) {device.status === 'active' ? '🟢' : '🔴'}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {!isConnected && (
                      <p className="text-xs text-yellow-600 text-center mb-2">
                        WebSocket not connected. Please wait...
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleStartShift}
              className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center gap-2 mx-auto"
            >
              <Play className="w-5 h-5" />
              Start My Shift
            </button>
          </div>

          {shiftHistory.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <History className="w-5 h-5" />
                Shift History
              </h3>
              <div className="space-y-4">
                {shiftHistory.slice(0, 10).map((shift) => (
                  <div
                    key={shift.id}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(shift.startedAt)} - {shift.endedAt ? formatDate(shift.endedAt) : 'Active'}
                        </p>
                        <p className="text-sm text-gray-600">
                          Duration: {formatDuration(shift.startedAt, shift.endedAt)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Status: {shift.status}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showStartModal && (
        <ShiftStartModal
          employeeId={employeeId}
          onClose={() => setShowStartModal(false)}
          onStart={handleStartShiftWithSelection}
        />
      )}
    </div>
  );
};

export default EmployeeShift;

